/**
 * Make element an element sticky on the vertical axis in JS.
 *
 * Usage :
 *  * Import the class, and setup your sticky elements.
 *  * You also have to set `top` or `bottom` in your CSS to have the correct desired offset.
 */

import { throttle } from 'throttle-debounce';

export default class Sticky {
  /**
   *
   * @param {*} $node The node which will be stick
   * @param {*} $refererNode The node we will refer to control sticky behavior
   */
  constructor($node, $refererNode) {
    const _ = this;

    // Elements

    _.$node = $node;
    _.$refererNode = $refererNode;
    // Clone node to reserve space when $node is fixed (out of the flow)
    _.$placeholderNode = null;

    // Variables

    // Metrics of sticky node
    _.nodeRect = null;
    // Metrics of referer node
    _.refererRect = null;
    // The range in which we need to fixed the sticky node
    _.range = { start: 0, end: 0 };
    // Current requestAnimationFrame ID
    _.rafID = null;
    // Current mode of position
    // (null: default, 'static': stuck to an edge by translation, 'fixed': fixed to the viewport)
    _.positionMode = null;
    // The edge on which we stuck the node ('top' or 'bottom')
    _.anchor = null;
    // Is there any requestAnimationFrame pending?
    _.IS_IDLE = true;
    // Are we currently listening the scroll?
    _.IS_LISTENING_SCROLL = false;

    // Binds
    _.handleScroll = _.handleScroll.bind(_);
    _.handleResize = _.handleResize.bind(_);
    _.boundControl = _.control.bind(_);
  }

  // Setup all required behaviors
  mount() {
    const _ = this;

    _.$placeholderNode = document.createElement('div');
    _.$node.style.willChange = 'position, transform, width';

    if (_.$node.offsetParent !== null && _.$refererNode.offsetHeight > _.$node.offsetHeight) {
      _.updateVars();
      _.requestControl();
      _.listenScroll();
    }

    window.addEventListener('resize', throttle(250, _.handleResize));
  }

  // Detach and clean all behaviors
  dismount() {
    const _ = this;

    _.clearStickyPosition();
    _.$node.style.willChange = '';

    cancelAnimationFrame(_.rafID);

    _.unlistenScroll();
    window.removeEventListener('resize', throttle(250, _.handleResize));
  }

  // Update dimensions variables (mostly on resize)
  updateVars() {
    const _ = this;

    // Ensure the top and bottom values are computed on IE/Edge
    _.$node.style.position = 'relative';
    const nodeComputedStyles = window.getComputedStyle(_.$node);
    _.$node.style.position = '';


    let nodeOffset = 0;
    _.nodeRect = _.$node.getBoundingClientRect();
    _.refererRect = _.$refererNode.getBoundingClientRect();

    // Begin with the bottom test because on Edge the computed bottom
    // when not setted is 'auto' (where top is '0px')
    if (nodeComputedStyles.bottom !== 'auto') {
      nodeOffset = parseInt(nodeComputedStyles.bottom, 10);
      _.range.start = (_.refererRect.top + window.pageYOffset) + (_.nodeRect.height - window.innerHeight) + nodeOffset;
      _.range.end = (_.refererRect.top + window.pageYOffset) + _.refererRect.height - window.innerHeight + nodeOffset;
      _.anchor = 'bottom';
    } else if (nodeComputedStyles.top !== 'auto') {
      nodeOffset = parseInt(nodeComputedStyles.top, 10);
      _.range.start = (_.refererRect.top + window.pageYOffset) - nodeOffset;
      _.range.end = (_.refererRect.top + window.pageYOffset) + _.refererRect.height - _.nodeRect.height - nodeOffset;
      _.anchor = 'top';
    }

    // Clone the dimensions of the sticky node onto the placeholder
    _.$placeholderNode.style.width = `${_.nodeRect.width}px`;
    _.$placeholderNode.style.height = `${_.nodeRect.height}px`;
    _.$placeholderNode.style.marginTop = nodeComputedStyles.marginTop;
    _.$placeholderNode.style.marginBottom = nodeComputedStyles.marginBottom;
    _.$placeholderNode.style.marginLeft = nodeComputedStyles.marginLeft;
    _.$placeholderNode.style.marginRight = nodeComputedStyles.marginRight;
  }

  // Activate scroll behaviors
  listenScroll() {
    const _ = this;

    window.addEventListener('scroll', _.handleScroll);
    _.IS_LISTENING_SCROLL = true;
  }

  // Disable scroll behaviors
  unlistenScroll() {
    const _ = this;

    window.removeEventListener('scroll', _.handleScroll);
    _.IS_LISTENING_SCROLL = false;
  }

  // Request update on scroll
  handleScroll() {
    const _ = this;

    _.requestControl();
  }

  // Update metrics and elements on resize
  handleResize() {
    const _ = this;

    _.refresh();
  }

  // Check if we still need sticky behavior and so, update metrics variables
  // or disable scroll events
  refresh() {
    const _ = this;

    cancelAnimationFrame(_.rafID);
    _.IS_IDLE = true;

    _.clearStickyPosition();

    if (_.$node.offsetParent !== null && _.$refererNode.offsetHeight > _.$node.offsetHeight) {
      _.updateVars();
      _.requestControl();

      if (!_.IS_LISTENING_SCROLL) {
        _.listenScroll();
      }
    } else if (_.IS_LISTENING_SCROLL) {
      _.unlistenScroll();
    }
  }

  // Request an update through requestAnimationFrame
  requestControl() {
    const _ = this;

    if (_.IS_IDLE) {
      _.IS_IDLE = false;
      _.rafID = requestAnimationFrame(_.boundControl);
    }
  }

  // Update position of the sticky node if needed
  control() {
    const _ = this;

    // Above start point : static at the top
    if (
      _.positionMode !== 'static'
      && ((window.pageYOffset <= _.range.start && _.anchor === 'top') || (window.pageYOffset >= _.range.end && _.anchor === 'bottom'))
    ) {
      if (_.$placeholderNode.parentNode) {
        _.$node.parentNode.removeChild(_.$placeholderNode);
      }
      _.$node.style.position = '';
      _.$node.style.width = '';
      _.$node.style.transform = 'translate(0, 0)';
      _.positionMode = 'static';
    }

    // Under end point : static at the bottom
    if (
      _.positionMode !== 'static'
      && ((window.pageYOffset >= _.range.end && _.anchor === 'top') || (window.pageYOffset <= _.range.start && _.anchor === 'bottom'))
    ) {
      if (_.$placeholderNode.parentNode) {
        _.$node.parentNode.removeChild(_.$placeholderNode);
      }

      _.$node.style.position = '';
      _.$node.style.width = '';
      if (_.anchor === 'top') {
        _.$node.style.transform = `translate(0, ${_.refererRect.height - _.nodeRect.height}px)`;
      } else {
        _.$node.style.transform = `translate(0, -${_.refererRect.height - _.nodeRect.height}px)`;
      }
      _.positionMode = 'static';
    }

    // Between start and end point : fixed
    if (window.pageYOffset > _.range.start && window.pageYOffset < _.range.end && _.positionMode !== 'fixed') {
      _.$node.parentNode.insertBefore(_.$placeholderNode, _.$node);

      _.$node.style.transform = '';
      _.$node.style.position = 'fixed';
      _.$node.style.width = `${_.nodeRect.width}px`;
      _.positionMode = 'fixed';
    }

    _.IS_IDLE = true;
  }

  // Clear current modifications
  clearStickyPosition() {
    const _ = this;

    if (_.$placeholderNode.parentNode) {
      _.$node.parentNode.removeChild(_.$placeholderNode);
    }
    _.$node.style.position = '';
    _.$node.style.width = '';
    _.$node.style.transform = '';
    _.positionMode = null;
  }
}
